'use client';
import Image from '@/components/Image';
import Switch from '@/components/Form/Switch';
import { useJump } from '@/components/MagicJump';
import Popover from '@/components/Popover';
import { useReporting } from '@/hooks/useReporting';
import { useUserWayWidget } from '@/hooks/useUserWayWidget';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import { colors, font } from '@/theme/zeelool.theme';
import { IS_DE_FR } from '@/utils/constants';
import { env } from '@/utils/env';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { Badge } from '@mui/material';
import { WapMoreOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import dynamic from 'next/dynamic';
import NextImage from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { type ChangeEvent, Suspense, useEffect, useRef, useState } from 'react';
import { HeaderProps } from '.';
import AccountIcon from './components/AccountIcon';
import CartIcon from './components/CartIcon';
import PcTopBarCarousel from './components/PcTopBarCarousel';
import SideNavWrapper from './components/SideNavWrapper.pc';
import SearchInput from './components/SearchComponent/SearchInput';
import SearchPopupPC from './components/SearchComponent/SearchPopup.pc';
import { TopBarSiteConfig } from './components/SiteConfig';
import { useKlarnaPaymentPromotion } from '@/components/Pay/KlarnaPromotion/useKlarnaPaymentPromotion';
import { shouldShowTermsUpdate } from './terms-update';
const SideNavTablet = dynamic(() => import('./components/SideNav.Tablet'), { ssr: false });

const BRAND_NAME = 'ZEELOOL';
type QuickEntryItem = NonNullable<HeaderProps['quickEntryData']['details']>[number];

const Header = (
  props: HeaderProps & {
    cartProductNum: number | string;
    isShowHeader: boolean;
    isDarkMode: boolean;
    isShowDtBannerByPage: boolean;
    loadSearchDataIfNeeded: () => Promise<void>;
    isBot?: boolean;
  },
) => {
  const { isShowKlarnaPromotion, text_1, klarna_text } = useKlarnaPaymentPromotion();
  const { isShowHeader, isDarkMode, isShowDtBannerByPage, loadSearchDataIfNeeded } = props;
  const publicResourceData = useStore((state) => state.publicResourceData);
  const showTermsUpdate = shouldShowTermsUpdate(publicResourceData?.info?.timestamp);
  const blindMode = useStore((state) => state.waiMode);
  const setBlindMode = useStore((state) => state.setWaiMode);
  const { openUserWayWidget, closeUserWayWidget } = useUserWayWidget();
  const wishNum = useStore((state) => state.wishNum);
  const t = useTranslations('layout.header');
  const tA11y = useTranslations('layout.header.a11y');
  const tDialog = useTranslations('dialog');
  const tSiteConfig = useTranslations('layout.siteConfig');
  const tFloat = useTranslations('layout.rightBottomFloating');
  const router = useRouter();
  const token = useStore((state) => state.token);
  const setShowLoginRegisterDialog = useStore((state) => state.setShowLoginRegisterDialog);
  const [showSearchPopup, setShowSearchPopup] = useState(false);
  const [isSearchInputFocus, setIsSearchInputFocus] = useState(false);
  const searchPopupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { trackEvent } = useReporting();
  const { cnName } = useRouteMetadata();
  const toJumpTarget = useJump();
  const currencyList = useStore((state) => state.currencyList);
  const currency = currencyList.find((item) => item.name === env.NEXT_PUBLIC_DEFAULT_CURRENCY);
  const [isOpenTabletSideNav, setIsOpenTabletSideNav] = useState(false);
  const [activeTabletNavIndex, setActiveTabletNavIndex] = useState<number | null>(null);
  // 清理定时器，避免内存泄漏
  useEffect(() => {
    return () => {
      if (searchPopupTimeoutRef.current) {
        clearTimeout(searchPopupTimeoutRef.current);
        searchPopupTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const desktopMedia = window.matchMedia('(min-width: 1281px)');
    const closeTabletNavigationOnDesktop = (event: MediaQueryListEvent) => {
      if (!event.matches) return;
      setActiveTabletNavIndex(null);
      setIsOpenTabletSideNav(false);
    };

    desktopMedia.addEventListener('change', closeTabletNavigationOnDesktop);
    return () => desktopMedia.removeEventListener('change', closeTabletNavigationOnDesktop);
  }, []);

  const handleWishClick = () => {
    trackEvent(
      {
        eventName: 'TopNavTab_Click',
        properties: {
          tab_name: '心愿单',
          url_path: window.location.href,
          page_name: cnName, // 使用路由元数据中的中文名称
          tab_index: 4,
        },
      },
      [ProviderType.SENSORS],
    );
    if (token) {
      router.push('/account/my-wishlist');
    } else {
      setShowLoginRegisterDialog({
        show: true,
        eventType: 'wish_register',
      });
    }
  };
  const publicFreeShipping = (publicResourceData?.config_names as any)
    ?.product_content_free_shipping_member;
  const publicHalfPrice = (publicResourceData?.config_names as any)
    ?.product_content_half_price_member;
  const hasPublicFreeShipping = publicFreeShipping?.status === 1;
  const hasPublicHalfPrice =
    publicHalfPrice?.status === 1 &&
    !!publicHalfPrice?.rule &&
    publicHalfPrice.rule.rule_flag === 1;
  const hasTopBarPromotion = hasPublicFreeShipping || hasPublicHalfPrice || isShowKlarnaPromotion;

  const publicFreeShippingTitle = () => {
    if (publicFreeShipping.status === 1) {
      let title = publicFreeShipping.title || '';
      const rule = publicFreeShipping.rule || {};
      const ordinaryFreeThreshold = rule.ordinary_free_threshold;
      if (title.includes('{threshold_price}') && ordinaryFreeThreshold) {
        title = title.replace('{threshold_price}', ordinaryFreeThreshold);
        title = title.replace(
          '{symbol_prefix}',
          rule.symbol_prefix ?? currency?.symbol_prefix ?? '',
        );
        title = title.replace(
          '{symbol_suffix}',
          rule.symbol_suffix ?? currency?.symbol_suffix ?? '',
        );
      }
      return title;
    }
    return publicFreeShipping.title;
  };

  const publicFreeShippingContent = () => {
    if (publicFreeShipping.status === 1) {
      let content = publicFreeShipping.content || '';
      const rule = publicFreeShipping.rule || {};
      if (
        content.includes('{threshold_price}') &&
        publicFreeShipping.rule.ordinary_free_threshold
      ) {
        content = content.replace(
          '{threshold_price}',
          publicFreeShipping.rule.ordinary_free_threshold,
        );
        content = content.replace(
          '{symbol_prefix}',
          rule.symbol_prefix ?? currency?.symbol_prefix ?? '',
        );
        content = content.replace(
          '{symbol_suffix}',
          rule.symbol_suffix ?? currency?.symbol_suffix ?? '',
        );
      }

      if (
        content.includes('{ordinary_free_country}') &&
        publicFreeShipping.rule.ordinary_free_country
      ) {
        content = content.replace(
          '{ordinary_free_country}',
          publicFreeShipping.rule.ordinary_free_country,
        );
      }

      if (
        content.includes('{ordinary_free_threshold}') &&
        publicFreeShipping.rule.ordinary_free_threshold
      ) {
        content = content.replace(
          '{ordinary_free_threshold}',
          publicFreeShipping.rule.ordinary_free_threshold,
        );
      }

      return content;
    }
    return publicFreeShipping.content;
  };

  const publicHalfPriceTitle = () => {
    if (publicHalfPrice?.rule?.rule_flag === 1) {
      let title = publicHalfPrice.title || '';
      // 替换占位符 {discount_rate} 为实际的折扣率
      if (title.includes('{discount_rate}') && publicHalfPrice.rule.discount_rate) {
        title = title.replace('{discount_rate}', publicHalfPrice.rule.discount_rate);
      }
      return title;
    }
    return publicHalfPrice.title;
  };

  const publicHalfPriceContent = () => {
    if (publicHalfPrice?.rule?.rule_flag === 1) {
      let content = publicHalfPrice.content || '';
      // 替换占位符 {discount_rate} 为实际的折扣率
      if (content.includes('{discount_rate}') && publicHalfPrice.rule.discount_rate) {
        content = content.replace('{discount_rate}', publicHalfPrice.rule.discount_rate);
      }

      if (content.includes('{threshold_price}') && publicHalfPrice.rule.threshold_price) {
        content = content.replace(
          '{threshold_price}',
          formatPriceNumberWithSymbol(publicHalfPrice.rule.threshold_price, currency),
        );
      }

      if (content.includes('{restrict_single}') && publicHalfPrice.rule.restrict_single) {
        content = content.replace('{restrict_single}', publicHalfPrice.rule.restrict_single);
      }
      return content;
    }
    return publicHalfPrice.content;
  };

  const SDSearchEntryBtn_Click = () => {
    trackEvent(
      {
        eventName: 'SearchEntryBtn_Click',
        properties: {
          page_name: cnName,
          url_path: window.location.href,
        },
      },
      [ProviderType.SENSORS],
    );
    trackEvent(
      {
        eventName: 'TopNavTab_Click',
        properties: {
          tab_name: '搜索',
          page_name: cnName,
          url_path: window.location.href,
        },
      },
      [ProviderType.SENSORS],
    );
  };

  const closeTabletSideNav = () => {
    setActiveTabletNavIndex(null);
    setIsOpenTabletSideNav(false);
  };

  const handleQuickEntryClick = (item: QuickEntryItem) => {
    trackEvent(
      {
        eventName: 'TopNavTab_Click',
        properties: {
          tab_name: item.quick_title,
          url_path: window.location.href,
          page_name: cnName,
          tab_index: '',
        },
      },
      [ProviderType.SENSORS],
    );
    toJumpTarget({
      jump_type: item.jump_type,
      Jump_target: item.Jump_target,
    });
  };

  const handleAccessibilityChange = (_event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (checked) {
      openUserWayWidget();
    } else {
      closeUserWayWidget();
    }
    setBlindMode(checked);
    trackEvent(
      {
        eventName: 'TopNavTab_Click',
        properties: {
          page_name: cnName,
          url_path: window.location.href,
          tab_name: `Accessibility_${checked ? 'Open' : 'Close'}`,
          first_level_category: '',
        },
      },
      [ProviderType.SENSORS],
    );
  };

  const handleTabletSideNavTriggerClick = () => {
    if (!isOpenTabletSideNav) {
      setActiveTabletNavIndex(null);
      setIsOpenTabletSideNav(true);
      trackEvent(
        {
          eventName: 'TopNavTab_Click',
          properties: {
            tab_name: '左侧菜单按钮',
            page_name: cnName,
            url_path: window.location.href,
            tab_index: 0,
          },
        },
        [ProviderType.SENSORS],
      );
      return;
    }

    if (activeTabletNavIndex !== null) {
      setActiveTabletNavIndex(null);
      return;
    }

    closeTabletSideNav();
  };

  // 打开平板侧栏后，PC Header 保持设计稿中的白底与深色图标。
  const effectiveDarkMode = isDarkMode && !isOpenTabletSideNav;
  const iconColorClass = effectiveDarkMode ? 'text-white' : 'text-primary-black';
  return (
    <div
      id="header"
      className={`sticky top-0 z-[400] ${!isShowHeader ? 'hidden' : ''} ${
        effectiveDarkMode
          ? 'bg-[rgba(0,0,0,1)] backdrop-blur-[25px] shadow-none'
          : 'bg-primary-white shadow-none'
      }`}
    >
      <Link href="/accessibility-statement" className="sr-only">
        Accessibility Statement
      </Link>
      {!isDarkMode && (
        <ul className="flex h-[40px] w-full items-center justify-between bg-primary-black px-[48px] text-white">
          <PcTopBarCarousel
            dtInfo={props.dtInfo}
            hasPromotion={hasTopBarPromotion}
            showDingTong={!!props.dtInfo && isShowDtBannerByPage}
          >
            {hasPublicFreeShipping && (
              <div className="relative flex items-center whitespace-nowrap font-br text-[12px] font-normal leading-[18px]">
                {publicFreeShippingTitle()}
                <Popover
                  id="free-shipping-popover"
                  interactionMode="hover-only"
                  content={
                    <div className="py-[15px] px-[20px]">
                      <div
                        className="text-[12px] leading-[30px] text-[#808080] break-words"
                        dangerouslySetInnerHTML={{
                          __html: publicFreeShippingContent().replace(/\n/g, '<br/>'),
                        }}
                      />
                    </div>
                  }
                  sx={{
                    '& .MuiPopover-paper': {
                      boxShadow: '0px 4px 12px 0px rgba(0,0,0,0.08)',
                      transform: 'translate(-114px, 11px)',
                    },
                  }}
                  triggerComponent={
                    <button
                      type="button"
                      aria-label={publicFreeShippingTitle()}
                      className="ml-[6px] flex size-[16px] shrink-0 items-center justify-center border-0 bg-transparent p-0"
                    >
                      <NextImage
                        src="/zeelool-next/pc-navigation/describe.svg"
                        alt=""
                        width={16}
                        height={16}
                        className="size-[16px]"
                        unoptimized
                        aria-hidden
                      />
                    </button>
                  }
                  width={400}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                  }}
                  // slotProps={{
                  //   paper: {
                  //     style: {
                  //       transform: 'translate(-114px, 11px) ',
                  //     },
                  //   },
                  // }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                  }}
                  noPadding
                />
              </div>
            )}
            {hasPublicHalfPrice && (
              <div className="relative flex items-center whitespace-nowrap font-br text-[12px] font-normal leading-[40px]">
                {publicHalfPriceTitle()}
                <Popover
                  id="half-price-popover"
                  interactionMode="hover-only"
                  content={
                    <div className="py-[15px] px-[20px]">
                      <div
                        className="text-[12px] leading-[30px] text-[#808080] break-words"
                        dangerouslySetInnerHTML={{
                          __html: publicHalfPriceContent().replace(/\n/g, '<br/>'),
                        }}
                      />
                    </div>
                  }
                  sx={{
                    '& .MuiPopover-paper': {
                      boxShadow: '0px 4px 12px 0px rgba(0,0,0,0.08)',
                    },
                  }}
                  triggerComponent={
                    <button
                      type="button"
                      aria-label={tA11y('halfPriceOfferDetails')}
                      className="ml-[6px] flex size-[16px] shrink-0 items-center justify-center border-0 bg-transparent p-0"
                    >
                      <NextImage
                        src="/zeelool-next/pc-navigation/describe.svg"
                        alt=""
                        width={16}
                        height={16}
                        className="size-[16px]"
                        unoptimized
                        aria-hidden
                      />
                    </button>
                  }
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  width={IS_DE_FR ? 440 : 400}
                  noPadding
                />
              </div>
            )}
            {isShowKlarnaPromotion && (
              <div className="relative flex items-center whitespace-nowrap font-br text-[12px] font-normal leading-[18px]">
                {text_1}
                {klarna_text && (
                  <Popover
                    id="klarna-promotion-header-popover"
                    interactionMode="hover-only"
                    content={
                      <div className="py-[15px] px-[20px] text-[12px] leading-[18px] text-[#808080] whitespace-pre-line break-words">
                        {klarna_text}
                      </div>
                    }
                    sx={{
                      '& .MuiPopover-paper': {
                        boxShadow: '0px 4px 12px 0px rgba(0,0,0,0.08)',
                      },
                    }}
                    triggerComponent={
                      <button
                        type="button"
                        aria-label={tA11y('klarnaPromotionDetails')}
                        className="ml-[6px] flex size-[16px] shrink-0 items-center justify-center border-0 bg-transparent p-0"
                      >
                        <NextImage
                          src="/zeelool-next/pc-navigation/describe.svg"
                          alt=""
                          width={16}
                          height={16}
                          className="size-[16px]"
                          unoptimized
                          aria-hidden
                        />
                      </button>
                    }
                    width={400}
                    noPadding
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'center',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'center',
                    }}
                  />
                )}
              </div>
            )}
          </PcTopBarCarousel>
          <li className="ml-[24px] flex h-full shrink-0 items-center justify-center text-[12px] leading-[14px] transition-all duration-300">
            {showTermsUpdate && (
              <span
                className="mr-[16px] cursor-pointer whitespace-nowrap opacity-60"
                onClick={() => {
                  trackEvent(
                    {
                      eventName: 'TopNavTab_Click',
                      properties: {
                        tab_name: 'Terms Update',
                        url_path: window.location.href,
                        page_name: cnName,
                        tab_index: '',
                      },
                    },
                    [ProviderType.SENSORS],
                  );
                  toJumpTarget({
                    jump_type: 4,
                    Jump_target: 'https://www.zeelool.com/terms-conditions',
                  });
                }}
              >
                Terms Update
              </span>
            )}
            <div className="hidden items-center lg:flex">
              {props.quickEntryData?.details?.map((item) => (
                <span
                  key={item.quick_title}
                  className="mr-[16px] cursor-pointer whitespace-nowrap opacity-60"
                  onClick={() => handleQuickEntryClick(item)}
                >
                  {item.quick_title}
                </span>
              ))}
            </div>
            <div className="hidden items-center gap-[4px] whitespace-nowrap lg:flex">
              <span className="opacity-60">{tSiteConfig('accessibility')}</span>
              <Switch
                checked={blindMode}
                width={26}
                height={16}
                sx={{
                  '& .MuiSwitch-track': {
                    backgroundColor: '#eee',
                    opacity: 0.4,
                  },
                }}
                aria-label={tSiteConfig('accessibility')}
                onChange={handleAccessibilityChange}
              />
            </div>
            <div className="ml-[24px] flex items-center">
              <Suspense fallback={null}>
                <TopBarSiteConfig />
              </Suspense>
            </div>
          </li>
        </ul>
      )}
      <ul
        className={`relative flex h-[64px] items-center justify-between border-b-1 border-b-solid px-[48px] min-[1281px]:h-[60px] min-[1281px]:pl-[44px] min-[1281px]:pr-[48px] ${effectiveDarkMode ? 'border-b-black' : 'border-b-[#dce0e8]'}`}
      >
        <li className="flex-1 w-0 justify-start flex items-center min-[1281px]:self-stretch">
          <button
            type="button"
            aria-label={
              !isOpenTabletSideNav
                ? tA11y('openMenu')
                : activeTabletNavIndex !== null
                  ? tSiteConfig('backAriaLabel')
                  : tDialog('closeAriaLabel')
            }
            aria-expanded={isOpenTabletSideNav}
            aria-controls="tablet-side-nav-panel"
            className="mr-[20px] flex size-[24px] cursor-pointer items-center justify-center border-0 bg-transparent p-0 min-[1281px]:hidden"
            onClick={handleTabletSideNavTriggerClick}
          >
            {!isOpenTabletSideNav ? (
              <WapMoreOutlined className={`text-[24px] ${iconColorClass}`} aria-hidden />
            ) : (
              <NextImage
                src={
                  activeTabletNavIndex !== null
                    ? '/zeelool-next/wap-navigation/header/back.svg'
                    : '/zeelool-next/wap-navigation/header/close.svg'
                }
                width={24}
                height={24}
                alt=""
                aria-hidden
                unoptimized
              />
            )}
          </button>
          <Link
            prefetch={false}
            href="/"
            aria-label={BRAND_NAME}
            className="relative h-[48px] w-[158px] overflow-hidden flex-shrink-0"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (window.location.pathname === '/') {
                return;
              }
              trackEvent(
                {
                  eventName: 'TopNavTab_Click',
                  properties: {
                    tab_name: 'Logo',
                    page_name: cnName,
                    url_path: window.location.href,
                    tab_index: 2,
                  },
                },
                [ProviderType.SENSORS],
              );
              router.push('/');
            }}
          >
            {props?.logoData?.logo_image ? (
              <Image
                className="block w-[158px] h-auto object-contain"
                src={effectiveDarkMode ? '/white-logo.png' : props.logoData.logo_image}
                alt={BRAND_NAME}
                width={158}
                height={48}
              />
            ) : (
              <div className="w-[158px]" />
            )}
            {!!props?.logoData?.logo_sub_image && (
              <Image
                className="absolute block w-[158px] h-auto left-0 z-10 object-contain"
                src={props.logoData.logo_sub_image}
                alt={BRAND_NAME}
                width={158}
                height={48}
              />
            )}
          </Link>
          <SideNavWrapper
            navData={props.headerNavList}
            isDarkMode={effectiveDarkMode}
            isBot={props.isBot ?? false}
          />
        </li>
        <li className="flex items-center justify-center h-full">
          <div
            className={`relative mr-[24px] flex h-[40px] w-[280px] shrink-0 items-center rounded-[24px] box-border ${effectiveDarkMode ? 'bg-transparent' : 'bg-[#f7f7f7]'} text-[14px] text-[#6c6c6c]`}
          >
            <Suspense fallback={null}>
              <SearchInput
                onClose={() => {
                  setShowSearchPopup(false);
                  setIsSearchInputFocus(false);
                }}
                isShowSearchBtn={false}
                isWeb
                isDarkMode={effectiveDarkMode}
                onFocus={async () => {
                  // 清除可能存在的延时关闭
                  if (searchPopupTimeoutRef.current) {
                    clearTimeout(searchPopupTimeoutRef.current);
                    searchPopupTimeoutRef.current = null;
                  }
                  // 按需加载搜索数据
                  loadSearchDataIfNeeded();
                  setShowSearchPopup(true);
                  setIsSearchInputFocus(true);
                  SDSearchEntryBtn_Click();
                }}
                onBlur={() => {
                  // 延时关闭，给用户时间点击弹窗内容
                  searchPopupTimeoutRef.current = setTimeout(() => {
                    setShowSearchPopup(false);
                    setIsSearchInputFocus(false);
                  }, 150); // 150ms延时
                }}
                onInputChange={(value) => {
                  // 当有输入内容时关闭Popover，避免与自动补全冲突
                  if (value && value.trim().length > 0) {
                    setShowSearchPopup(false);
                  } else if ((!value || value.trim().length === 0) && isSearchInputFocus) {
                    // 当输入为空时，如果输入框有焦点，则显示Popover
                    setShowSearchPopup(true);
                  }
                }}
              />
            </Suspense>

            <SearchPopupPC show={showSearchPopup} onClose={() => setShowSearchPopup(false)} />
          </div>
          <ul className="flex items-center h-full box-border">
            <AccountIcon
              isDarkMode={effectiveDarkMode}
              deviceType={props.deviceType}
              icon={
                <NextImage
                  src="/zeelool-next/pc-navigation/mine.svg"
                  alt=""
                  width={24}
                  height={24}
                  unoptimized
                  aria-hidden
                  className={`size-[24px] shrink-0 ${effectiveDarkMode ? 'brightness-0 invert' : ''}`}
                />
              }
            />
            <li className="pl-[24px] relative">
              <button
                type="button"
                className="p-0 m-0 border-0 bg-transparent cursor-pointer flex items-center justify-center h-full"
                onClick={handleWishClick}
                aria-label={t('wishlist')}
              >
                <NextImage
                  src="/zeelool-next/pc-navigation/collect.svg"
                  alt=""
                  width={24}
                  height={24}
                  unoptimized
                  aria-hidden
                  className={`size-[24px] shrink-0 ${effectiveDarkMode ? 'brightness-0 invert' : ''}`}
                />
              </button>
              {wishNum > 0 && (
                <Badge
                  sx={{
                    position: 'absolute',
                    top: '-8px',
                    left: '39px',
                    minWidth: '18px',
                    height: '18px',
                    lineHeight: '18px',
                    padding: '0 5px',
                    boxSizing: 'border-box',
                    borderRadius: '10px',
                    textAlign: 'center',
                    fontSize: '12px',
                    color: '#fff',
                    backgroundColor: colors.primaryPurple,
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontFamily: font.br,
                    '& .MuiBadge-badge.MuiBadge-standard.MuiBadge-invisible': {
                      display: 'none',
                    },
                  }}
                >
                  <span
                    style={{
                      ...(wishNum > 99 && { transform: 'translateY(-20%)' }),
                    }}
                  >
                    {wishNum > 99 ? '...' : wishNum}
                  </span>
                </Badge>
              )}
            </li>
            <CartIcon
              isDarkMode={effectiveDarkMode}
              cartProductNum={props.cartProductNum}
              deviceType={props.deviceType}
              icon={
                <NextImage
                  src="/zeelool-next/pc-navigation/shoppingCart.svg"
                  alt=""
                  width={24}
                  height={24}
                  unoptimized
                  aria-hidden
                  className={`size-[24px] shrink-0 ${effectiveDarkMode ? 'brightness-0 invert' : ''}`}
                />
              }
            />
          </ul>
        </li>
      </ul>
      <SideNavTablet
        show={isOpenTabletSideNav}
        navData={props.headerNavList}
        activeNavIndex={activeTabletNavIndex}
        onActiveNavIndexChange={setActiveTabletNavIndex}
        onClose={closeTabletSideNav}
        primaryFooter={
          <div
            data-testid="tablet-primary-navigation-footer"
            className="flex shrink-0 items-center gap-[16px] border-t border-[#d1d9e0] bg-white px-[48px] py-[16px] font-br text-[12px] leading-[14px] text-[#282828] lg:hidden"
          >
            {props.quickEntryData?.details?.map((item) => (
              <button
                key={item.quick_title}
                type="button"
                className="cursor-pointer whitespace-nowrap border-0 bg-transparent p-0 font-inherit text-inherit"
                onClick={() => {
                  closeTabletSideNav();
                  handleQuickEntryClick(item);
                }}
              >
                {item.quick_title}
              </button>
            ))}
            <div className="flex items-center gap-[4px] whitespace-nowrap">
              <span>{tSiteConfig('accessibility')}</span>
              <Switch
                checked={blindMode}
                width={26}
                height={16}
                sx={{
                  '& .MuiSwitch-track': {
                    border: 0,
                    backgroundColor: '#d1d9e0',
                  },
                }}
                aria-label={tSiteConfig('accessibility')}
                onChange={handleAccessibilityChange}
              />
            </div>
          </div>
        }
      />
    </div>
  );
};

export default Header;
